import type {
    EntityPatch,
    HistoryDomainAdapter,
    HistoryEntitySnapshot,
    HistoryPatchApplyInput,
} from '..';
import {
    areSketchGroupRuntimeSnapshotsEqual,
    type SketchGroupRuntimeHistorySnapshot,
    type SketchGroupRuntimeHistoryStore,
} from './SketchGroupRuntimeHistoryTypes';

/**
 * 草图组运行时历史 adapter ID。
 */
export const SKETCH_GROUP_RUNTIME_HISTORY_ADAPTER_ID = 'history-domain:sketch-group-runtime';

/**
 * 应用草图组运行时快照。
 *
 * 设计说明：
 * 1. 设计树删除/复制草图会产生存在性变化，missing 快照必须通过宿主删除门面处理；
 * 2. existing 快照继续走运行时快照恢复，保持整组几何、约束、轮廓和显示态一致；
 * 3. adapter 只依赖 HistoryStore 抽象，避免反向耦合 Viewer 或 SketchGroupManager。
 *
 * @param input 补丁应用输入。
 */
function applySketchGroupRuntimeSnapshot(
    input: HistoryPatchApplyInput<SketchGroupRuntimeHistorySnapshot, SketchGroupRuntimeHistoryStore>,
): void {
    const snapshot = input.snapshot;

    if (!snapshot.exists) {
        input.context.deleteSketchGroupRuntimeSnapshot(input.patch.entityId);
        return;
    }

    if (!snapshot.data) {
        throw new Error(`草图组 ${input.patch.entityId} 缺少运行时快照数据。`);
    }

    if (snapshot.data.persistent !== false) {
        throw new Error(`草图组 ${input.patch.entityId} 的运行时快照不得进入持久化历史。`);
    }

    input.context.restoreSketchGroupRuntimeSnapshot(snapshot.data);
}

/**
 * 比较草图组运行时历史快照。
 *
 * @param left 左侧快照。
 * @param right 右侧快照。
 * @returns 两个快照是否语义相同。
 */
function areRuntimeHistorySnapshotsEqual(
    left: HistoryEntitySnapshot<SketchGroupRuntimeHistorySnapshot>,
    right: HistoryEntitySnapshot<SketchGroupRuntimeHistorySnapshot>,
): boolean {
    if (left.exists !== right.exists) {
        return false;
    }

    if (!left.exists || !right.exists) {
        return true;
    }

    if (!left.data || !right.data) {
        return false;
    }

    return areSketchGroupRuntimeSnapshotsEqual(left.data, right.data);
}

/**
 * 创建草图组运行时历史 adapter。
 *
 * @returns 草图组运行时历史 adapter。
 */
export function createSketchGroupRuntimeHistoryAdapter(): HistoryDomainAdapter<
    SketchGroupRuntimeHistorySnapshot,
    SketchGroupRuntimeHistoryStore
> {
    return {
        adapterId: SKETCH_GROUP_RUNTIME_HISTORY_ADAPTER_ID,
        kind: 'sketch',
        applySnapshot: applySketchGroupRuntimeSnapshot,
        areSnapshotsEqual: areRuntimeHistorySnapshotsEqual as HistoryDomainAdapter<
            SketchGroupRuntimeHistorySnapshot,
            SketchGroupRuntimeHistoryStore
        >['areSnapshotsEqual'],
    };
}

/**
 * 创建草图组运行时历史补丁。
 *
 * @param patch 补丁字段。
 * @returns 草图组运行时历史补丁。
 */
export function createSketchGroupRuntimeHistoryPatch(
    patch: Omit<EntityPatch<SketchGroupRuntimeHistorySnapshot>, 'adapterId' | 'entityKind'>,
): EntityPatch<SketchGroupRuntimeHistorySnapshot> {
    return {
        ...patch,
        entityKind: 'sketch',
        adapterId: SKETCH_GROUP_RUNTIME_HISTORY_ADAPTER_ID,
    };
}
